<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 class="mb-0">Branch Inventory</h3>
          <div class="d-flex align-items-center gap-2">
            <select v-model="selectedBranchId" @change="fetchInventory" class="form-control form-control-sm" style="width:200px;">
              <option value="">Select Branch</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
            <input v-model="search" @input="fetchInventory" type="text" placeholder="Search product..." class="form-control form-control-sm" style="width:200px;" />
          </div>
        </div>

        <div v-if="!selectedBranchId" class="p-4 text-center text-muted">
          Select a branch to view inventory.
        </div>

        <div v-else>
          <table class="table table-sm table-flush">
            <thead class="thead-light">
              <tr>
                <th>Product</th>
                <th style="width:120px;">Qty in Stock</th>
                <th style="width:100px;">Adjust</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="3" class="text-center py-3">Loading...</td></tr>
              <tr v-else-if="!inventory.length"><td colspan="3" class="text-center text-muted py-3">No products found.</td></tr>
              <tr v-for="row in inventory" :key="row.product_id">
                <td>{{ row.product_name }}</td>
                <td>
                  <span :class="row.quantity > 0 ? 'text-success font-weight-bold' : 'text-danger'">
                    {{ row.quantity }}
                  </span>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline-primary py-0 px-2" @click="openAdjust(row)">Adjust</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Adjust Modal -->
    <div v-if="adjustModal"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;display:flex;align-items:center;justify-content:center;">
      <div class="bg-white rounded shadow-lg p-4" style="width:420px;max-width:95vw;">
        <h5 class="mb-3">Adjust Stock — {{ adjustRow && adjustRow.product_name }}</h5>
        <p class="text-muted text-sm mb-3">Current stock: <strong>{{ adjustRow && adjustRow.quantity }}</strong></p>

        <div class="mb-3">
          <label class="form-control-label d-block mb-2">Reason</label>
          <div class="d-flex flex-wrap gap-2">
            <button v-for="r in reasons" :key="r.type"
              @click="selectReason(r)"
              :class="['btn btn-sm', selectedReason && selectedReason.type === r.type ? r.activeClass : r.outlineClass]"
              style="margin:2px;">
              {{ r.label }}
            </button>
          </div>
        </div>

        <div v-if="selectedReason" class="mb-3">
          <label class="form-control-label">
            {{ selectedReason.mode === 'absolute' ? 'New Stock Quantity' : selectedReason.mode === 'add' ? 'Quantity to Add' : 'Quantity to Deduct' }}
          </label>
          <input v-model.number="adjustQty" type="number" min="0" class="form-control form-control-sm"
            :placeholder="selectedReason.mode === 'absolute' ? 'Enter new total quantity' : 'Enter quantity'" />
          <small class="text-muted">{{ selectedReason.hint }}</small>
        </div>

        <div class="mb-3">
          <label class="form-control-label">Notes (optional)</label>
          <input v-model="adjustNotes" type="text" class="form-control form-control-sm" placeholder="Reason / notes..." />
        </div>

        <p v-if="adjustError" class="text-danger text-sm mb-2">{{ adjustError }}</p>
        <div class="d-flex gap-2">
          <button @click="closeAdjust" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="saveAdjust" :disabled="saving || !selectedReason" class="btn btn-primary btn-sm flex-fill">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";

const BASE = endpoints.LARAVEL_BASE_URL;

const REASONS = [
  { type: 'opening_stock', label: 'Opening Stock',     mode: 'absolute', hint: 'Set the initial stock count.',           outlineClass: 'btn-outline-primary',  activeClass: 'btn-primary' },
  { type: 'adjustment',    label: 'Manual Correction', mode: 'absolute', hint: 'Correct the stock to the actual count.', outlineClass: 'btn-outline-secondary', activeClass: 'btn-secondary' },
  { type: 'surplus',       label: 'Surplus Found',     mode: 'add',      hint: 'Add quantity found in excess.',          outlineClass: 'btn-outline-success',   activeClass: 'btn-success' },
  { type: 'damage',        label: 'Damage / Spoilage', mode: 'deduct',   hint: 'Deduct damaged items.',                  outlineClass: 'btn-outline-warning',   activeClass: 'btn-warning' },
  { type: 'expired',       label: 'Expired / Rusted',  mode: 'deduct',   hint: 'Deduct expired or rusted items.',        outlineClass: 'btn-outline-warning',   activeClass: 'btn-warning' },
  { type: 'loss',          label: 'Loss / Theft',      mode: 'deduct',   hint: 'Deduct items lost or stolen.',           outlineClass: 'btn-outline-danger',    activeClass: 'btn-danger' },
];

export default {
  components: { 'header-component': headerComponent },
  data() {
    return {
      branches: [],
      selectedBranchId: '',
      search: '',
      inventory: [],
      loading: false,
      adjustModal: false,
      adjustRow: null,
      reasons: REASONS,
      selectedReason: null,
      adjustQty: 0,
      adjustNotes: '',
      adjustError: '',
      saving: false,
    };
  },
  mounted() {
    apiHelper.call('get', BASE + '/api/v1/branches')
      .then(res => { this.branches = res.data.data || res.data; })
      .catch(() => {});
  },
  methods: {
    fetchInventory() {
      if (!this.selectedBranchId) return;
      this.loading = true;
      var params = 'branch_id=' + this.selectedBranchId;
      if (this.search) params += '&search=' + encodeURIComponent(this.search);
      apiHelper.call('get', BASE + '/api/v1/branch-inventory?' + params)
        .then(res => { this.inventory = res.data.inventory || []; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    openAdjust(row) {
      this.adjustRow = row;
      this.selectedReason = null;
      this.adjustQty = 0;
      this.adjustNotes = '';
      this.adjustError = '';
      this.adjustModal = true;
    },
    selectReason(r) {
      this.selectedReason = r;
      this.adjustQty = r.mode === 'absolute' ? (this.adjustRow ? this.adjustRow.quantity : 0) : 0;
      this.adjustError = '';
    },
    closeAdjust() {
      this.adjustModal = false;
      this.adjustRow = null;
      this.selectedReason = null;
    },
    saveAdjust() {
      this.adjustError = '';
      if (!this.selectedReason) { this.adjustError = 'Please select a reason.'; return; }
      if (this.adjustQty < 0) { this.adjustError = 'Quantity cannot be negative.'; return; }
      this.saving = true;
      apiHelper.call('post', BASE + '/api/v1/branch-inventory/adjust', {
        branch_id:       this.selectedBranchId,
        product_id:      this.adjustRow.product_id,
        quantity:        this.adjustQty,
        adjustment_type: this.selectedReason.type,
        notes:           this.adjustNotes || null,
      })
        .then(() => {
          window.notify({ message: 'Stock updated.', type: 'success', title: 'Done' });
          this.closeAdjust();
          this.fetchInventory();
        })
        .catch(err => {
          this.adjustError = (err && err.response && err.response.data && err.response.data.message) || 'Failed to update stock.';
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>
